import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Plus, Trash2, Pencil } from "lucide-react";
import TableContainer from "Common/TableContainer";
import { Link, useNavigate } from "react-router-dom";
import DeleteModal from "Common/DeleteModal";
import Modal from "Common/Components/Modal";
import Select from "react-select";
import selectStyles from "Common/constants/selectStyles";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import Swal from "sweetalert2";
import { ROLE_LABEL_BY_NAME } from "Common/constants/roles";
import { apiErrorMessage } from "Common/constants/apiErrors";

const BASE_URL = "https://portal-api.mahanpanel.com";

const extractList = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

// برچسب فارسی نقش‌ها از منبع مرکزی نقش‌ها می‌آید تا با منو و مجوزها یکی بماند.
// اگر سرور نقش تازه‌ای بدهد که در نگاشت نیست، همان نام خام سرور نمایش داده می‌شود.
const roleLabels = ROLE_LABEL_BY_NAME;

interface Role {
  id: number;
  name: string;
}

interface AppUser {
  id: number;
  username: string;
  fullname: string;
  email: string;
  phone_number: string;
  disabled: boolean;
  role_id: number;
  role: string;
  created_at_jalali: string;
}

// ترجمه‌ی خطاهای سرور به فارسی از ماژول مشترک انجام می‌شود.
const getErrorMessage = (error: any, fallback: string) =>
  apiErrorMessage(error, fallback);

const authHeaders = () => {
  const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
  return { Authorization: `Bearer ${token}` };
};

const Index = () => {
  const navigate = useNavigate();

  // نقش کاربر جاری طبق مستند از GET /auth/me خوانده می‌شود (نه از دیکد توکن).
  // role_id: 0 = superadmin, 1 = admin. این صفحه فقط برای این دو نقش است.
  const [currentUserRoleId, setCurrentUserRoleId] = useState<number | null>(null);
  const [meLoading, setMeLoading] = useState<boolean>(true);
  const isSuperAdmin = currentUserRoleId === 0;
  const isAdmin = currentUserRoleId === 0 || currentUserRoleId === 1;

  useEffect(() => {
    const fetchMe = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/auth/me`, { headers: authHeaders() });
        setCurrentUserRoleId(res.data?.role_id ?? null);
      } catch (error) {
        console.error("Error fetching current user role:", error);
      } finally {
        setMeLoading(false);
      }
    };
    fetchMe();
  }, []);

  const [users, setUsers] = useState<AppUser[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [reload, setReload] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<"default" | "asc" | "desc">("default");
  const changeServerSort = useCallback((nextSortBy: string | undefined, nextSortOrder: "default" | "asc" | "desc") => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setCurrentPage(1);
  }, []);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [roles, setRoles] = useState<Role[]>([]);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AppUser | null>(null);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  // ========== دریافت لیست نقش‌ها ==========
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/roles/`, { headers: authHeaders() });
        setRoles(extractList(res.data));
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };
    fetchRoles();
  }, []);

  // ========== دریافت لیست کاربران ==========
  useEffect(() => {
    if (!isAdmin) return;
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const params: any = {
          page: currentPage,
          size: pageSize,
          ...(sortOrder === "default" ? { sort_order: "default" } : { sort_by: sortBy, sort_order: sortOrder }),
        };
        if (searchTerm) params.search = searchTerm;

        const res = await axios.get(`${BASE_URL}/users/`, {
          headers: authHeaders(),
          params,
        });

        const items = extractList(res.data);
        
        setUsers(items);
        const total = res.data?.total ?? items.length;
        setTotalItems(total);
        setTotalPages(res.data?.pages || Math.max(Math.ceil(total / pageSize), 1));
      } catch (error: any) {
        console.error("Error fetching users:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        Swal.fire("خطا", apiErrorMessage(error, "مشکلی در دریافت لیست کاربران رخ داده است."), "error");
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [reload, currentPage, pageSize, searchTerm, navigate, isAdmin, sortBy, sortOrder]);

  // ========== حذف ==========
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${BASE_URL}/users/${deleteTarget.id}`, { headers: authHeaders() });
      Swal.fire("عملیات موفق", "کاربر با موفقیت حذف شد", "success");
      setReload(!reload);
      setDeleteModal(false);
      setDeleteTarget(null);
    } catch (error: any) {
      console.error("Delete user error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      Swal.fire("خطا", getErrorMessage(error, "خطا در حذف کاربر"), "error");
    }
  };

  // ========== فرم ثبت/ویرایش ==========
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: "",
      password: "",
      fullname: "",
      email: "",
      phone_number: "",
      disabled: false,
      role_id: "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("لطفاً نام کاربری را وارد کنید"),
      password: editMode
        ? Yup.string()
        : Yup.string().required("لطفاً رمز عبور را وارد کنید"),
      fullname: Yup.string().required("لطفاً نام و نام خانوادگی را وارد کنید"),
      email: Yup.string().email("ایمیل معتبر نیست").required("لطفاً ایمیل را وارد کنید"),
      phone_number: Yup.string().required("لطفاً شماره همراه را وارد کنید"),
      role_id: Yup.string().required("لطفاً نقش را انتخاب کنید"),
    }),
    onSubmit: async (values) => {
      const payload: any = {
        username: values.username,
        fullname: values.fullname,
        email: values.email,
        phone_number: values.phone_number,
        disabled: values.disabled,
        role_id: Number(values.role_id),
      };
      if (values.password) payload.password = values.password;
      else if (!editMode) payload.password = "";

      try {
        if (editMode && editingId) {
          await axios.patch(`${BASE_URL}/users/${editingId}`, payload, {
            headers: { "Content-Type": "application/json", ...authHeaders() },
          });
          Swal.fire("عملیات موفق", `کاربر «${values.username}» بروزرسانی شد`, "success");
        } else {
          await axios.post(`${BASE_URL}/users/`, payload, {
            headers: { "Content-Type": "application/json", ...authHeaders() },
          });
          Swal.fire("عملیات موفق", `کاربر «${values.username}» ثبت شد`, "success");
        }
        setReload(!reload);
        closeModal();
      } catch (error: any) {
        console.error("Submit user error:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        Swal.fire("خطا", getErrorMessage(error, "مشکل در ثبت یا ویرایش کاربر"), "error");
      }
    },
  });

  const openCreateModal = () => {
    setEditMode(false);
    setEditingId(null);
    validation.resetForm();
    setShowModal(true);
  };

  const handleEdit = (row: AppUser) => {
    setEditMode(true);
    setEditingId(row.id);
    validation.setValues({
      username: row.username || "",
      password: "",
      fullname: row.fullname || "",
      email: row.email || "",
      phone_number: row.phone_number || "",
      disabled: !!row.disabled,
      role_id: String(row.role_id),
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditMode(false);
    setEditingId(null);
    validation.resetForm();
  };

  // ادمین (غیر سوپرادمین) نه گزینه‌ی سوپرادمین را در فرم می‌بیند و نه می‌تواند حساب سوپرادمین
  // را مدیریت کند؛ این کار فقط برای سوپرادمین است.
  const roleOptions = useMemo(
    () =>
      roles
        .filter((r) => isSuperAdmin || r.name !== "superadmin")
        .map((r) => ({ value: String(r.id), label: roleLabels[r.name] || r.name })),
    [roles, isSuperAdmin]
  );

  const canManageRow = (row: AppUser) => isSuperAdmin || row.role_id !== 0;

  const columns = useMemo(
    () => [
      {
        header: "نام کاربری",
        accessorKey: "username",
        cell: (cell: any) => <span className="font-medium">{cell.getValue()}</span>,
      },
      { header: "نام و نام خانوادگی", accessorKey: "fullname" },
      { header: "ایمیل", accessorKey: "email" },
      { header: "شماره همراه", accessorKey: "phone_number" },
      {
        header: "نقش",
        accessorKey: "role",
        meta: { serverSortBy: "role_name" },
        cell: (cell: any) => roleLabels[cell.getValue()] || cell.getValue(),
      },
      {
        header: "وضعیت حساب",
        accessorKey: "disabled",
        cell: (cell: any) => (
          <span
            className={`text-xs font-medium px-2 py-1 rounded-full ${!cell.getValue()
              ? "bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400"
              : "bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400"
              }`}
          >
            {cell.getValue() ? "غیرفعال" : "فعال"}
          </span>
        ),
      },
      {
        header: "عملیات",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const row: AppUser = cell.row.original;
          if (!canManageRow(row)) return <span className="text-slate-400 text-xs">—</span>;
          return (
            <div className="flex gap-3">
              <Link to="#!" onClick={() => handleEdit(row)} title="ویرایش">
                <Pencil className="size-4 text-blue-500 hover:text-blue-700" />
              </Link>
              <Link
                to="#!"
                onClick={() => {
                  setDeleteTarget(row);
                  setDeleteModal(true);
                }}
                title="حذف"
              >
                <Trash2 className="size-4 text-red-500 hover:text-red-700" />
              </Link>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSuperAdmin]
  );

  // if (meLoading) {
  //   return (
  //     <div className="flex justify-center items-center h-32">
  //       <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-custom-500 border-t-transparent"></div>
  //     </div>
  //   );
  // }

  // if (!isAdmin) {
  //   return (
  //     <React.Fragment>
  //       <BreadCrumb pageTitle="مدیریت کاربران" title="مدیریت کاربران" />
  //       <div className="card">
  //         <div className="card-body py-16 text-center text-slate-400 dark:text-zink-300">
  //           دسترسی به این صفحه فقط برای ادمین و سوپرادمین است.
  //         </div>
  //       </div>
  //     </React.Fragment>
  //   );
  // }

  return (
    <React.Fragment>
      <BreadCrumb pageTitle="مدیریت کاربران" title="مدیریت کاربران" />
      <DeleteModal
        show={deleteModal}
        onHide={() => {
          setDeleteModal(false);
          setDeleteTarget(null);
        }}
        onDelete={handleDelete}
      />

      <div className="card">
        <div className="card-body">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <h6 className="text-15 grow">
              مجموع کاربران (<b className="total-Employs">{totalItems}</b>)
            </h6>
            <div className="flex gap-2 items-center">
              <input
                type="text"
                placeholder="جست‌وجو در نام کاربری، نام یا ایمیل..."
                className="form-input text-sm w-56"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="shrink-0">
              <button
                onClick={openCreateModal}
                type="button"
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600"
              >
                <span className="align-middle rtl">ثبت کاربر جدید</span>{" "}
                <Plus className="inline-block size-4 rtl" />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-custom-500 border-t-transparent"></div>
            </div>
          ) : users.length > 0 ? (
            <TableContainer
              isPagination={true}
              showGlobalSearch={false}
              columns={columns}
              data={users}
              serverSorting={{ sortBy, sortOrder, onChange: changeServerSort }}
              customPageSize={pageSize}
              serverPagination={{
                currentPage,
                totalPages,
                onPageChange: setCurrentPage,
                pageSize,
                onPageSizeChange: (size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                },
              }}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80"
              PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
            />
          ) : (
            <LoadingCounter count={1} text="هیچ کاربری ثبت نشده است" />
          )}
        </div>
      </div>

      {/* ========== مودال ثبت/ویرایش کاربر ========== */}
      <Modal
        show={showModal}
        onHide={closeModal}
        modal-center="true"
        dialogClassName="w-screen md:w-[34rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500"
        >
          <Modal.Title className="text-16">
            {editMode ? "ویرایش کاربر" : "ثبت کاربر جدید"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form onSubmit={validation.handleSubmit}>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <div>
                <label className="block text-base font-medium mb-1">نام کاربری:</label>
                <input
                  type="text"
                  className="form-input w-full"
                  name="username"
                  autoComplete="off"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.username}
                />
                {validation.touched.username && validation.errors.username && (
                  <p className="text-red-400 mt-1 text-sm">{validation.errors.username}</p>
                )}
              </div>

              <div>
                <label className="block text-base font-medium mb-1">
                  رمز عبور{editMode && " (خالی بگذارید تا تغییر نکند)"}:
                </label>
                <input
                  type="password"
                  className="form-input w-full"
                  name="password"
                  autoComplete="new-password"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.password}
                />
                {validation.touched.password && validation.errors.password && (
                  <p className="text-red-400 mt-1 text-sm">{validation.errors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-base font-medium mb-1">نام و نام خانوادگی:</label>
                <input
                  type="text"
                  className="form-input w-full"
                  name="fullname"
                  autoComplete="off"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.fullname}
                />
                {validation.touched.fullname && validation.errors.fullname && (
                  <p className="text-red-400 mt-1 text-sm">{validation.errors.fullname}</p>
                )}
              </div>

              <div>
                <label className="block text-base font-medium mb-1">ایمیل:</label>
                <input
                  type="email"
                  className="form-input w-full"
                  name="email"
                  autoComplete="off"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.email}
                />
                {validation.touched.email && validation.errors.email && (
                  <p className="text-red-400 mt-1 text-sm">{validation.errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-base font-medium mb-1">شماره همراه:</label>
                <input
                  type="text"
                  className="form-input w-full"
                  name="phone_number"
                  autoComplete="off"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.phone_number}
                />
                {validation.touched.phone_number && validation.errors.phone_number && (
                  <p className="text-red-400 mt-1 text-sm">{validation.errors.phone_number}</p>
                )}
              </div>

              <div>
                <label className="block text-base font-medium mb-1">نقش:</label>
                <Select
                  options={roleOptions}
                  value={roleOptions.find((o) => o.value === validation.values.role_id) || null}
                  onChange={(opt: any) => validation.setFieldValue("role_id", opt?.value || "")}
                  placeholder="انتخاب نقش..."
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  noOptionsMessage={() => "موردی یافت نشد"}
                />
                {validation.touched.role_id && validation.errors.role_id && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.role_id === "string" ? validation.errors.role_id : ""}
                  </p>
                )}
              </div>

              <div className="xl:col-span-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="user-disabled"
                  checked={validation.values.disabled}
                  onChange={(e) => validation.setFieldValue("disabled", e.target.checked)}
                />
                <label htmlFor="user-disabled" className="text-base font-medium">
                  غیرفعال بودن حساب
                </label>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className="flex justify-center p-4 border-t dark:border-zink-500">
          <button
            type="button"
            onClick={() => validation.handleSubmit()}
            className="btn min-w-[9rem] px-8 py-2.5 mt-2 font-bold text-white bg-custom-500 border-custom-500 hover:bg-custom-600"
          >
            {editMode ? "ذخیره تغییرات" : "ثبت"}
          </button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default Index;
